import React from 'react';
import { Col, Divider, Form, Input, Row, Select, Typography } from 'antd';
// Hooks
import { useGetColleges, useGetLeagues, useGetTeams } from 'hooks';
// Types
import { Sports_sports } from 'api/sports/types/Sports';
// Helpers
import { getExternalTalentSportOptions } from 'helpers/externalTalents';

const { Text, Title } = Typography;
const { Option } = Select;

const careerCardStyle: React.CSSProperties = {
  background: '#fafafa',
  border: '1px solid #f0f0f0',
  borderRadius: 12,
  padding: 16,
};

type CareerDetailsSectionProps = {
  currentSport?: string | null;
  loadingSports?: boolean;
  sports: Sports_sports[];
};

const CareerDetailsSection = ({
  currentSport,
  loadingSports = false,
  sports,
}: CareerDetailsSectionProps): JSX.Element => {
  const { data: collegesData } = useGetColleges();
  const { data: leaguesData } = useGetLeagues();
  const { data: teamsData } = useGetTeams();
  const sportOptions = getExternalTalentSportOptions(sports, currentSport);

  return (
    <>
      <Divider />

      <Title level={5}>Career Details</Title>

      <div style={careerCardStyle}>
        <Form.Item label="Sport" name="sport">
          <Select
            allowClear
            loading={loadingSports}
            optionFilterProp="children"
            placeholder="Select sport"
            showSearch
          >
            {sportOptions.map((sport) => (
              <Option key={`${sport.id}-${sport.name}`} value={sport.name}>
                {sport.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Text style={{ display: 'block', marginBottom: 12 }} type="secondary">
          Use standardized catalog values when possible, or fall back to raw
          fields below.
        </Text>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="College / University" name="collegeUniversity">
              <Select allowClear optionFilterProp="children" showSearch>
                {collegesData?.getCollegesList?.map((college) => (
                  <Option key={college.id} value={college.name}>
                    {college.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label="League" name="league">
              <Select allowClear optionFilterProp="children" showSearch>
                {leaguesData?.getLeaguesList?.map((league) => (
                  <Option key={league.id} value={league.name}>
                    {league.sport
                      ? `${league.name} (${league.sport})`
                      : league.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label="Team" name="team">
              <Select allowClear optionFilterProp="children" showSearch>
                {teamsData?.getTeamsList?.map((team) => (
                  <Option key={team.id} value={team.name}>
                    {[team.name, team.sport, team.league]
                      .filter(Boolean)
                      .join(' | ')}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="College / University raw"
              name="collegeUniversityRaw"
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label="League raw" name="leagueRaw">
              <Input />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item label="Team raw" name="teamRaw">
              <Input />
            </Form.Item>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default CareerDetailsSection;
